// ─────────────────────────────────────────────────────────────────────────────
// featureFlag.ts – Server-driven kill switch
//
// Polls /api/ai/feature_flag on widget open and every N minutes thereafter.
// If the server replies { enabled: false } or { killSwitch: true }, the
// assistant hides itself with a small notice. This lets ops disable the
// feature globally without a deploy.
//
// Until the backend is up, an absent endpoint is treated as "enabled" so the
// FE doesn't break local dev.
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect, useState } from "react";

export interface FeatureFlagState {
	enabled: boolean;
	killSwitch: boolean;
	message?: string;
	checkedAt: number;
}

const POLL_INTERVAL_MS = 5 * 60_000; // 5 min

const DEFAULT_STATE: FeatureFlagState = {
	enabled: true,
	killSwitch: false,
	checkedAt: 0,
};

async function fetchFlag(
	endpoint: string,
	signal: AbortSignal
): Promise<{ state: FeatureFlagState; endpointMissing: boolean }> {
	// Fail-open by default: only an explicit `{ enabled: false }` or
	// `{ killSwitch: true }` from a successful response should hide the bot.
	// Endpoint missing, auth errors, network failures, malformed JSON — all
	// treated as "enabled" so a flaky/unimplemented kill-switch doesn't block
	// the feature for everyone. The auth gate in App.js already protects
	// against unauthorised access at the chat-call level.
	try {
		const res = await fetch(endpoint, {
			method: "GET",
			credentials: "include",
			signal,
		});
		if (!res.ok) {
			return {
				state: { ...DEFAULT_STATE, checkedAt: Date.now() },
				endpointMissing: res.status === 404,
			};
		}
		const data = (await res.json()) as Partial<FeatureFlagState>;
		return {
			state: {
				enabled: data.enabled ?? true,
				killSwitch: data.killSwitch ?? false,
				message: data.message,
				checkedAt: Date.now(),
			},
			endpointMissing: false,
		};
	} catch {
		return {
			state: { ...DEFAULT_STATE, checkedAt: Date.now() },
			endpointMissing: false,
		};
	}
}

export function useFeatureFlag(aiProxyEndpoint?: string): FeatureFlagState {
	const [state, setState] = useState<FeatureFlagState>(DEFAULT_STATE);

	useEffect(() => {
		if (!aiProxyEndpoint) return;
		const endpoint = aiProxyEndpoint.replace(/\/+$/, "") + "/feature_flag";
		const controller = new AbortController();

		let cancelled = false;
		let intervalId: ReturnType<typeof setInterval> | null = null;

		const poll = async () => {
			const { state: next, endpointMissing } = await fetchFlag(
				endpoint,
				controller.signal
			);
			if (cancelled) return;
			setState(next);
			// Endpoint not deployed (404) — stop polling. The bot stays enabled
			// (fail-open) and we don't burn a request every 5 min for nothing.
			if (endpointMissing && intervalId !== null) {
				clearInterval(intervalId);
				intervalId = null;
			}
		};

		poll();
		intervalId = setInterval(poll, POLL_INTERVAL_MS);

		return () => {
			cancelled = true;
			controller.abort();
			if (intervalId !== null) clearInterval(intervalId);
		};
	}, [aiProxyEndpoint]);

	return state;
}
